export type RGB = readonly [number, number, number];

export type Mode = "crochet" | "cross-stitch";

export interface TemplateSettings {
  mode: Mode;
  grid: number;          // cells per side
  paletteSize: number;   // max distinct colors
  mergeDistance: number; // RGB euclidean distance threshold for palette merge
  cellPx: number;        // explicit cell pixel size (only used when autoCellPx is false)
  autoCellPx: boolean;   // when true, derive cellPx from grid to keep output canvas readable
  showSymbols: boolean;
  majorLineEvery: number;
}

export interface TemplateResult {
  grid: number;
  indices: Uint8Array;
  palette: RGB[];
  counts: number[];
}

export interface ModeProfile {
  label: string;
  gridMin: number;
  gridMax: number;
  gridStep: number;
  paletteMin: number;
  paletteMax: number;
  paletteDefault: number;
  defaultGrid: number;
  majorLineEvery: number;
  defaultCellPx: number;
  // Target output canvas long-edge in pixels for auto cell sizing.
  autoCanvasTarget: number;
  minCellPx: number;
  maxCellPx: number;
}

export const MODE_PROFILES: Record<Mode, ModeProfile> = {
  crochet: {
    label: "Crochet",
    gridMin: 10,
    gridMax: 40,
    gridStep: 1,
    paletteMin: 2,
    paletteMax: 10,
    paletteDefault: 6,
    defaultGrid: 25,
    majorLineEvery: 5,
    defaultCellPx: 32,
    autoCanvasTarget: 1500,
    minCellPx: 16,
    maxCellPx: 48,
  },
  "cross-stitch": {
    label: "Cross-stitch",
    gridMin: 50,
    gridMax: 300,
    gridStep: 10,
    paletteMin: 5,
    paletteMax: 30,
    paletteDefault: 15,
    defaultGrid: 120,
    majorLineEvery: 10,
    defaultCellPx: 16,
    autoCanvasTarget: 2800,
    minCellPx: 8,
    maxCellPx: 24,
  },
};

export const DEFAULT_SETTINGS: TemplateSettings = {
  mode: "crochet",
  grid: MODE_PROFILES.crochet.defaultGrid,
  paletteSize: MODE_PROFILES.crochet.paletteDefault,
  mergeDistance: 28,
  cellPx: MODE_PROFILES.crochet.defaultCellPx,
  autoCellPx: false,
  showSymbols: true,
  majorLineEvery: MODE_PROFILES.crochet.majorLineEvery,
};

/** Settings that should reset progress when changed (different geometry / palette). */
export function settingsFingerprint(s: TemplateSettings): string {
  return [
    s.mode,
    s.grid,
    s.paletteSize,
    s.mergeDistance,
    s.majorLineEvery,
  ].join(":");
}

/** Compute the effective cell pixel size honoring autoCellPx. */
export function effectiveCellPx(s: TemplateSettings): number {
  if (!s.autoCellPx) return s.cellPx;
  const profile = MODE_PROFILES[s.mode];
  const candidate = Math.floor(profile.autoCanvasTarget / s.grid);
  return Math.max(profile.minCellPx, Math.min(profile.maxCellPx, candidate));
}

/** Build a fresh settings object for a given mode using its profile defaults. */
export function settingsForMode(mode: Mode, base?: Partial<TemplateSettings>): TemplateSettings {
  const profile = MODE_PROFILES[mode];
  return {
    ...DEFAULT_SETTINGS,
    ...base,
    mode,
    grid: profile.defaultGrid,
    paletteSize: profile.paletteDefault,
    cellPx: profile.defaultCellPx,
    autoCellPx: mode === "cross-stitch",
    majorLineEvery: profile.majorLineEvery,
  };
}
